import { startFruitConfettiAnimation } from "./fruit-confetti-animation";
import {
  LeaderboardLeagueGroupConfig,
  LeaderboardLeagues,
  Tree,
  UserData,
  UserGamificationProperties,
} from "../../../constants/GlobalTypesAndEnumsAndConstants";
import { useGamificationStore, userStore } from "../../../zustand/globalStore";
import { updateUser } from "../../models/UserModel";
import { getDateFromDatabaseField, trackEvent } from "../helper-methods";

type LevelConfig = {
  xpNeededToReachNextLevel: number;
  treeUnlock?: Tree;
  buddyUnlock?: boolean; // true when a buddy is awarded
};

export const LEVEL_AT_WHICH_ALL_TREES_ARE_UNLOCKED = 16;

const LEVEL_TABLE: Record<number, LevelConfig> = {
  1: { xpNeededToReachNextLevel: 100 },
  2: { xpNeededToReachNextLevel: 125, treeUnlock: Tree.Banana },
  3: { xpNeededToReachNextLevel: 125 },
  4: { xpNeededToReachNextLevel: 200, treeUnlock: Tree.Ipe },
  5: { xpNeededToReachNextLevel: 200 },
  6: { xpNeededToReachNextLevel: 200 },
  7: { xpNeededToReachNextLevel: 400, treeUnlock: Tree.Banyan },
  8: { xpNeededToReachNextLevel: 400 },
  9: { xpNeededToReachNextLevel: 400 },
  10: { xpNeededToReachNextLevel: 400 },
  11: { xpNeededToReachNextLevel: 500, treeUnlock: Tree.DragonBlood },
  12: { xpNeededToReachNextLevel: 500 },
  13: { xpNeededToReachNextLevel: 500 },
  14: { xpNeededToReachNextLevel: 500 },
  15: { xpNeededToReachNextLevel: 500 },
  16: { xpNeededToReachNextLevel: 300, buddyUnlock: true, treeUnlock: Tree.Kapok }, // Buddy #1, go back to first tree
  17: { xpNeededToReachNextLevel: 300 },
  18: { xpNeededToReachNextLevel: 300 },
  19: { xpNeededToReachNextLevel: 300 },
  20: { xpNeededToReachNextLevel: 400, treeUnlock: Tree.Banana },
  21: { xpNeededToReachNextLevel: 400 },
  22: { xpNeededToReachNextLevel: 400 },
  23: { xpNeededToReachNextLevel: 400 },
  24: { xpNeededToReachNextLevel: 500, treeUnlock: Tree.Ipe },
  25: { xpNeededToReachNextLevel: 500 },
  26: { xpNeededToReachNextLevel: 500 },
  27: { xpNeededToReachNextLevel: 500 },
  28: { xpNeededToReachNextLevel: 500, treeUnlock: Tree.Banyan },
  29: { xpNeededToReachNextLevel: 500 },
  30: { xpNeededToReachNextLevel: 500 },
  31: { xpNeededToReachNextLevel: 500 },
  32: { xpNeededToReachNextLevel: 500, treeUnlock: Tree.DragonBlood },
  33: { xpNeededToReachNextLevel: 500 },
  34: { xpNeededToReachNextLevel: 500 },
  35: { xpNeededToReachNextLevel: 500 },
  36: { xpNeededToReachNextLevel: 500, buddyUnlock: true, treeUnlock: Tree.Kapok }, // Buddy #2, go back to first tree
  // the pattern from 17-36 will repeat indefinitely
};

const PATTERN_START = 17; // first row of the repeating block
const PATTERN_END = 36; // last row  of the repeating block
const PATTERN_LEN = PATTERN_END - PATTERN_START + 1;

/** Return the config for *any* level (1 … ∞). */
const getLevelConfig = (level: number): LevelConfig => {
  // ❶ If we have an explicit row (≤ 35) just use it:
  if (LEVEL_TABLE[level]) return { ...LEVEL_TABLE[level] };

  // ❷ Otherwise pick the matching row inside the 16‑35 pattern:
  const idxWithinPattern = (level - PATTERN_START) % PATTERN_LEN; // 0‑based
  const templateLevel = PATTERN_START + idxWithinPattern; // 16‑35
  return { ...LEVEL_TABLE[templateLevel] }; // copy, so callers can mutate safely
};

/**
 * Flushes the buffered XP (earned since the river was last displayed),
 * applies any resulting level‑ups (may chain), persists the new
 * gamification state, and returns progress‑bar percentages so the UI can
 * animate from the old fill state to the new one.
 *
 * @returns {percentBefore, percentAfter, levelsGained}
 */
export async function flushRiverXpAnimateRiverAndMaybeLevelUp(
  userId: string,
  xpEarnedFromReviewSession?: number
): Promise<{ percentBefore: number; percentAfter: number; levelsGained: number }> {
  const user = userStore.getState().currentUser;
  const gamificationProps = user?.gamification_properties;
  if (!gamificationProps) return { percentBefore: 0, percentAfter: 0, levelsGained: 0 };
  /* ------------------------------------------------------------------ */
  /* 1️⃣  Progress *before* adding the buffered XP                      */
  /* ------------------------------------------------------------------ */
  let percentBefore = gamificationProps.xp_earned_at_current_level / gamificationProps.xp_needed_to_reach_next_level;

  /* ------------------------------------------------------------------ */
  /* 2️⃣  Move buffered XP into the current-level counter               */
  /* ------------------------------------------------------------------ */
  let xpEarnedSinceRiverProgressWasLastSeen = gamificationProps.xp_earned_since_river_progress_was_last_seen;
  if (
    gamificationProps.current_level === 1 &&
    xpEarnedFromReviewSession &&
    xpEarnedFromReviewSession > xpEarnedSinceRiverProgressWasLastSeen &&
    xpEarnedFromReviewSession > gamificationProps.xp_earned_at_current_level
  ) {
    // fix a small bug where xp earned by 1st time users is not consistent
    xpEarnedSinceRiverProgressWasLastSeen = xpEarnedFromReviewSession;
  }

  gamificationProps.xp_earned_at_current_level += xpEarnedSinceRiverProgressWasLastSeen;
  gamificationProps.xp_earned_since_river_progress_was_last_seen = 0;

  /* local helpers ----------------------------------------------------- */
  const ANIMATION_DURATION_IN_SECONDS = 12; // lower this value if you want the animation to be faster
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  // Helper to publish animated state immediately
  const publishAnimatedState = () => {
    const progressStatuses = getProgressOfLevelsInCurrentPhaseUntilNextRewardUnlock(gamificationProps);
    const levelsUntil = progressStatuses.filter((status) => status !== "level completed").length;
    const nextRewardName = getNameOfNextRewardToUnlock(gamificationProps);

    useGamificationStore.setState({
      animatedCurrentLevel: gamificationProps.current_level,
      animatedXpNeededToReachNextLevel: gamificationProps.xp_needed_to_reach_next_level,
      animatedLevelsUntilNextRewardUnlock: levelsUntil,
      animatedNameOfNextRewardToUnlock: nextRewardName,
    });
  };

  const runAnimation = async (percentAfter: number, percentBefore: number) => {
    const ms = (percentAfter - percentBefore) * 1000 * ANIMATION_DURATION_IN_SECONDS;
    // if (ms <= 0) return; // nothing to animate
    useGamificationStore.setState({
      startRiverAnimationPercentage: percentBefore,
      endRiverAnimationPercentage: percentAfter,
      playRiverAnimationForTimeInMilliseconds: ms,
    });
    await sleep(ms + 500); // add 200ms to ensure the animation is complete
  };

  // Publish initial state right after transferring buffered XP
  publishAnimatedState();

  /* ------------------------------------------------------------------ */
  /* 3️⃣  First animation for the current level                         */
  /* ------------------------------------------------------------------ */
  let percentAfter = gamificationProps.xp_earned_at_current_level / gamificationProps.xp_needed_to_reach_next_level;
  percentAfter = Math.min(1, percentAfter);

  await runAnimation(percentAfter, percentBefore);

  //   percentBefore = percentAfter;

  /* ------------------------------------------------------------------ */
  /* 4️⃣  Handle one-or-many level-ups after the animation              */
  /* ------------------------------------------------------------------ */
  let levelsGained = 0;
  while (gamificationProps.xp_earned_at_current_level >= gamificationProps.xp_needed_to_reach_next_level) {
    const leftoverXp = gamificationProps.xp_earned_at_current_level - gamificationProps.xp_needed_to_reach_next_level;

    performLevelUp(gamificationProps, user); // mutates in-place
    levelsGained += 1;

    /* progress for the *new* level ----------------------------------- */
    gamificationProps.xp_earned_at_current_level = leftoverXp;

    // Publish animated state immediately after level-up with the new level info
    publishAnimatedState();

    // Reset animated XP counter to 0 for the new level
    useGamificationStore.setState({
      animatedXpEarnedAtCurrentLevel: 0,
    });

    percentAfter = gamificationProps.xp_earned_at_current_level / gamificationProps.xp_needed_to_reach_next_level;
    percentAfter = Math.min(1, percentAfter);
    await runAnimation(percentAfter, 0);
  }

  /* ------------------------------------------------------------------ */
  /* 5️⃣  Persist the final gamification state                          */
  /* ------------------------------------------------------------------ */
  await updateUser(userId, { gamification_properties: gamificationProps });

  // Reset animated values when animation sequence is complete
  useGamificationStore.getState().resetAnimatedValues();

  return { percentBefore, percentAfter, levelsGained };
}

/**
 * Increment the learner’s level and apply all rewards/unlocks that come
 * with the new level.
 *
 * Mutates `gamificationProps` in‑place.
 */
function performLevelUp(gamificationProps: UserGamificationProperties, user: UserData): void {
  /* ------------------------------------------------------------------ */
  /* 1️⃣  Move to the next level                                        */
  /* ------------------------------------------------------------------ */
  gamificationProps.current_level += 1;

  const levelConfig: LevelConfig = getLevelConfig(gamificationProps.current_level);

  /* ------------------------------------------------------------------ */
  /* 2️⃣  Update XP requirement for the new level                       */
  /* ------------------------------------------------------------------ */
  gamificationProps.xp_needed_to_reach_next_level = levelConfig.xpNeededToReachNextLevel;

  /* ------------------------------------------------------------------ */
  /* 3️⃣  Tree unlocks                                                  */
  /* ------------------------------------------------------------------ */
  if (levelConfig.treeUnlock) {
    gamificationProps.current_tree_being_used = levelConfig.treeUnlock;

    if (!gamificationProps.unlocked_trees.includes(levelConfig.treeUnlock)) {
      gamificationProps.unlocked_trees.push(levelConfig.treeUnlock);
    }

    // UI layer: trigger “Tree Unlocked” modal
    useGamificationStore.setState({ showRewardUnlockedModal: true, rewardToShow: levelConfig.treeUnlock });
  }

  /* ------------------------------------------------------------------ */
  /* 4️⃣  Buddy unlocks                                                 */
  /* ------------------------------------------------------------------ */
  if (levelConfig.buddyUnlock) {
    gamificationProps.number_of_buddies_unlocked += 1;

    // UI layer: trigger “Buddy Unlocked” modal
    useGamificationStore.setState({ showRewardUnlockedModal: true, rewardToShow: "buddy" });
  }

  /* ------------------------------------------------------------------ */
  /* 5️⃣  XP milestone tracking                                         */
  /* ------------------------------------------------------------------ */
  const XP_MILESTONES_DESC = [1000, 500, 200, 100, 50, 25]; // highest → lowest
  const newlyReachedMilestone = XP_MILESTONES_DESC.find(
    (milestone) =>
      gamificationProps.total_xp_earned >= milestone && (gamificationProps.last_xp_milestone_hit ?? 0) < milestone
  );

  if (newlyReachedMilestone) {
    gamificationProps.last_xp_milestone_hit = newlyReachedMilestone;
    // UI layer: celebrate milestone here if desired
  }
  // Calculate time since user creation. Use the defensive helper so a missing
  // `date_created` (e.g. partner-created users before backend backfill) does
  // not throw inside a level-up trackEvent path.
  const userCreatedDate = getDateFromDatabaseField(user.date_created);
  const currentDate = new Date();
  const timeDiffMs = currentDate.getTime() - userCreatedDate.getTime();

  const minutesUntilLevelReached = Math.floor(timeDiffMs / (1000 * 60));
  const hoursUntilLevelReached = Math.floor(timeDiffMs / (1000 * 60 * 60));
  const daysUntilLevelReached = Math.floor(timeDiffMs / (1000 * 60 * 60 * 24));

  trackEvent(
    "user leveled up",
    {
      levelReached: gamificationProps.current_level,
      totalXpEarned: gamificationProps.total_xp_earned,
      treeUnlocked: levelConfig.treeUnlock,
      buddyUnlocked: levelConfig.buddyUnlock,
      dateReached: new Date().toISOString(),
      minutesUntilLevelReached,
      hoursUntilLevelReached,
      daysUntilLevelReached,
    },
    user
  );
}

/* -------------------------------------------------------------------------- */
/*  Level‑phase progress helper                                               */
/* -------------------------------------------------------------------------- */

export type LevelProgressStatus = "level completed" | "level in progress" | "level not reached yet";

/**
 * Returns an array whose length equals the number of levels *after* the
 * learner’s most‑recent reward unlock up to and including the next reward
 * level.
 *
 * Each entry is one of:
 *  • "level completed"      – level < current_level
 *  • "level in progress"    – level === current_level
 *  • "level not reached yet"– level > current_level
 *
 * Example (current_level = 18, last unlock = 16, next unlock = 19):
 *   [ "level completed",  // 17
 *     "level in progress",// 18
 *     "level not reached yet" ] // 19
 */
export function getProgressOfLevelsInCurrentPhaseUntilNextRewardUnlock(
  gamificationProps: UserGamificationProperties
): LevelProgressStatus[] {
  const { current_level, xp_earned_at_current_level } = gamificationProps;

  /* ------------------------------------------------------------------ */
  /* 1️⃣  Identify the most‑recent reward unlock level                  */
  /* ------------------------------------------------------------------ */
  let lastRewardLevel = 1; // level 1 always unlocks Banana tree
  for (let lvl = current_level; lvl >= 1; lvl -= 1) {
    const levelConfig = getLevelConfig(lvl);
    if (levelConfig.treeUnlock || levelConfig.buddyUnlock) {
      lastRewardLevel = lvl;
      break;
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2️⃣  Identify the next reward unlock level                         */
  /* ------------------------------------------------------------------ */
  let nextRewardLevel = current_level + 1;
  while (true) {
    const levelConfig = getLevelConfig(nextRewardLevel);
    if (levelConfig.treeUnlock || levelConfig.buddyUnlock) break;
    nextRewardLevel += 1;
  }

  /* ------------------------------------------------------------------ */
  /* 3️⃣  Build progress array from (lastRewardLevel + 1)…nextRewardLevel */
  /* ------------------------------------------------------------------ */
  const phaseProgress: LevelProgressStatus[] = [];

  for (let lvl = lastRewardLevel; lvl <= nextRewardLevel - 1; lvl += 1) {
    if (lvl < current_level) {
      phaseProgress.push("level completed");
    } else if (lvl === current_level && xp_earned_at_current_level >= 1) {
      phaseProgress.push("level in progress");
    } else {
      phaseProgress.push("level not reached yet");
    }
  }

  return phaseProgress;
}

export type NextRewardName = Tree | "buddy";

/**
 * Returns the name of the next reward the learner will unlock.
 *
 *  • If the next reward is a tree, the function returns that `Tree` enum value
 *    (e.g. `Tree.Banyan`).
 *  • If the next reward is a buddy, it returns the string "buddy".
 */
export function getNameOfNextRewardToUnlock(gamificationProps: UserGamificationProperties): NextRewardName {
  const { current_level } = gamificationProps;

  let level = current_level + 1;

  while (true) {
    const cfg = getLevelConfig(level);

    if (cfg.buddyUnlock) return "buddy";
    if (cfg.treeUnlock) return cfg.treeUnlock; // will be a `Tree` enum value

    level += 1; // keep scanning until we find the next unlock
  }
}

/* -------------------------------------------------------------------------- */
/*  Convenience initialiser for brand‑new users                               */
/* -------------------------------------------------------------------------- */
export function defaultGamificationProps(old_xp_earned?: number | null): UserGamificationProperties {
  const levelConfigForLevel1 = getLevelConfig(1);

  const defaultGamificationProps: UserGamificationProperties = {
    character_team_selected: null,
    character_with_accessory_selected: null,

    current_level: 1,
    total_xp_earned: 0,
    xp_earned_at_current_level: 0,
    xp_needed_to_reach_next_level: levelConfigForLevel1.xpNeededToReachNextLevel,
    xp_earned_since_river_progress_was_last_seen: 0,

    number_of_buddies_unlocked: 0,
    current_tree_being_used: levelConfigForLevel1.treeUnlock ?? Tree.Kapok,
    unlocked_trees: [levelConfigForLevel1.treeUnlock ?? Tree.Kapok],

    last_xp_milestone_hit: 0,
    current_week_xp_earned: 0,
    all_time_xp_earned: 0,

    // for leaderboard leagues
    leaderboard_leagues_metadata: {
      current_league_rank_name: null,
      league_ranks_unlocked: [],
      group_leagues_participated_in: [],
      number_of_times_promoted: 0,
      number_of_times_demoted: 0,
      number_of_times_with_no_change_in_league: 0,
      current_group_id: null,
      last_group_id_user_participated_in: null,
      should_show_end_of_week_league_results: false,
      last_position_in_leaderboard: null,
    },
    badges_progress_and_stats: {
      questions_answered_badges: {
        number_of_questions_answered: 0,
        badges: [],
      },
      free_response_questions_answered_badges: {
        number_of_free_response_questions_answered: 0,
        badges: [],
      },
      exams_survived_badges: {
        number_of_exams_survived: 0,
        badges: [],
      },
      daily_reviews_badges: {
        number_of_daily_reviews_completed: 0,
        badges: [],
      },
      mistakes_corrected_badges: {
        number_of_mistakes_corrected: 0,
        badges: [],
      },
      ids_of_earned_badges_user_has_not_seen: [],
    },
  };

  if (old_xp_earned) {
    defaultGamificationProps.old_xp_earned = old_xp_earned;
  }

  return defaultGamificationProps;
}

// ... existing code ...
/* -------------------------------------------------------------------------- */
/*  Convenience initialiser for brand‑new users                               */

export function confettiWithFruitsAnimation(origin?: { x: number; y: number }, showBannaOnly?: boolean) {
  return startFruitConfettiAnimation({ origin, bananaOnly: showBannaOnly });
}

/**
 * Returns a week identifier in the format "YYYYWww" where weeks start on Monday 00:00 PT.
 * Useful for generating consistent week IDs for databases or logs.
 *
 * @param {Date | string} inputDate - The date for which the week ID should be calculated (defaults to current date/time)
 * @returns {string} - A week ID string like "2026W01"
 */
export function generateWeekIdentifierBasedOnMondayStartInPacificTime(
  inputDate: Date | string = new Date(),
  weeksAgo: number = 0
): string {
  // Convert the input date to a Date object
  const dateToCalculateWeekFor = new Date(inputDate);

  if (Number.isNaN(dateToCalculateWeekFor.getTime())) {
    throw new Error("generateWeekIdentifierBasedOnMondayStartInPacificTime received an invalid date");
  }

  const normalizedWeeksAgo = Number.isFinite(weeksAgo) ? Math.trunc(weeksAgo) : 0;
  const millisecondsPerWeek = 7 * 24 * 60 * 60 * 1000;
  const dateAdjustedByWeeks = new Date(dateToCalculateWeekFor.getTime() + normalizedWeeksAgo * millisecondsPerWeek);

  // Convert the date to Pacific Time (PT)
  const dateInPacificTime = new Date(dateAdjustedByWeeks.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));

  const yearOfInputDateInPT = dateInPacificTime.getFullYear();

  // Find January 1st of that year in Pacific Time
  const januaryFirstOfYearInPT = new Date(`${yearOfInputDateInPT}-01-01T00:00:00-08:00`);

  // Determine the first Monday of the year in PT
  const januaryFirstDayOfWeekInPT = januaryFirstOfYearInPT.getDay();
  const daysUntilFirstMonday = (7 + 1 - januaryFirstDayOfWeekInPT) % 7;
  const firstMondayOfYearInPT = new Date(januaryFirstOfYearInPT);
  firstMondayOfYearInPT.setDate(firstMondayOfYearInPT.getDate() + daysUntilFirstMonday);

  // If the date is before the first Monday, it belongs to the last week of the previous year
  if (dateInPacificTime < firstMondayOfYearInPT) {
    const lastDayOfPreviousYearUTC = new Date(`${yearOfInputDateInPT - 1}-12-31T12:00:00Z`);
    return generateWeekIdentifierBasedOnMondayStartInPacificTime(lastDayOfPreviousYearUTC);
  }

  // Calculate the number of days between the input date and the first Monday
  const numberOfDaysSinceFirstMonday = Math.floor(
    (dateInPacificTime.getTime() - firstMondayOfYearInPT.getTime()) / 86400000
  );

  // Calculate the week number (1-based) relative to the first Monday
  const weekNumberSinceFirstMonday = Math.floor(numberOfDaysSinceFirstMonday / 7) + 1;

  // Format week number as 2-digit string
  const formattedWeekNumber = String(weekNumberSinceFirstMonday).padStart(2, "0");

  // Generate final week ID
  const weekIdentifier = `${yearOfInputDateInPT}W${formattedWeekNumber}`;

  return weekIdentifier;
}

const PACIFIC_TIME_ZONE = "America/Los_Angeles";
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_HOUR = MINUTES_PER_HOUR * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
const SECONDS_PER_WEEK = 7 * SECONDS_PER_DAY;

type PacificTimeWeekday = "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat";

const PACIFIC_TIME_WEEKDAY_TO_NUMBER: Record<PacificTimeWeekday, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

type PacificTimeComponents = {
  weekdayNumber: number;
  hour: number;
  minute: number;
  second: number;
};

const pacificTimeFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: PACIFIC_TIME_ZONE,
  hour12: false,
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const getPacificTimeComponents = (referenceDate: Date): PacificTimeComponents => {
  const formattedParts = pacificTimeFormatter.formatToParts(referenceDate);

  const getPartValue = (type: string) => formattedParts.find((part) => part.type === type)?.value ?? "0";

  const weekdayValue = getPartValue("weekday") as PacificTimeWeekday | "0";

  const weekdayNumber =
    PACIFIC_TIME_WEEKDAY_TO_NUMBER[
      weekdayValue in PACIFIC_TIME_WEEKDAY_TO_NUMBER ? (weekdayValue as PacificTimeWeekday) : "Sun"
    ];

  const hour = Number.parseInt(getPartValue("hour"), 10);
  const minute = Number.parseInt(getPartValue("minute"), 10);
  const second = Number.parseInt(getPartValue("second"), 10);

  return {
    weekdayNumber,
    hour: Number.isNaN(hour) ? 0 : hour,
    minute: Number.isNaN(minute) ? 0 : minute,
    second: Number.isNaN(second) ? 0 : second,
  };
};

export type RemainingTimeUntilLeaderboardWeekEnds = { days: number } | { hours: string };

export function getTimeForFinishingCurrentWeekInPacificTime(
  referenceDate: Date = new Date()
): RemainingTimeUntilLeaderboardWeekEnds {
  const pacificTimeComponents = getPacificTimeComponents(referenceDate);

  // Compute remaining time until next Monday 00:00:00 PT
  // Convert elapsed seconds to be measured since Monday 00:00:00 PT
  const daysSinceMonday = pacificTimeComponents.weekdayNumber === 0 ? 6 : pacificTimeComponents.weekdayNumber - 1;
  const elapsedSecondsSinceMondayInPacificTime =
    daysSinceMonday * SECONDS_PER_DAY +
    pacificTimeComponents.hour * SECONDS_PER_HOUR +
    pacificTimeComponents.minute * SECONDS_PER_MINUTE +
    pacificTimeComponents.second;

  let remainingSecondsUntilCutoff = SECONDS_PER_WEEK - elapsedSecondsSinceMondayInPacificTime;
  // If exactly at Monday 00:00:00 PT, show a full week remaining
  if (remainingSecondsUntilCutoff <= 0) {
    remainingSecondsUntilCutoff += SECONDS_PER_WEEK;
  }
  const fullDaysRemaining = Math.floor(remainingSecondsUntilCutoff / SECONDS_PER_DAY);
  if (fullDaysRemaining >= 1) {
    return { days: fullDaysRemaining };
  }

  const totalMinutesRemaining = Math.ceil(remainingSecondsUntilCutoff / SECONDS_PER_MINUTE);
  const remainingHours = Math.floor(totalMinutesRemaining / MINUTES_PER_HOUR);
  const remainingMinutes = totalMinutesRemaining % MINUTES_PER_HOUR;
  const formattedHours = String(remainingHours).padStart(2, "0");
  const formattedMinutes = String(remainingMinutes).padStart(2, "0");

  return { hours: `${formattedHours}:${formattedMinutes}` };
}

export const LeaderboardLeaguesConfig: Record<LeaderboardLeagues, LeaderboardLeagueGroupConfig> = {
  [LeaderboardLeagues.SPROUT]: {
    leagueTier: 1,
    leagueIcon: "/images/leaderboard_leagues/sprout_league.svg",
    translationKey: "sprout_league",
    defaultTranslation: "sprout league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 20,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 0,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 10,
    league_name: LeaderboardLeagues.SPROUT,
  },
  [LeaderboardLeagues.LEAF]: {
    leagueTier: 2,
    leagueIcon: "/images/leaderboard_leagues/leaf_league.svg",
    translationKey: "leaf_league",
    defaultTranslation: "leaf league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 15,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 7,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 8,
    league_name: LeaderboardLeagues.LEAF,
  },
  [LeaderboardLeagues.FERN]: {
    leagueTier: 3,
    leagueIcon: "/images/leaderboard_leagues/fern_league.svg",
    translationKey: "fern_league",
    defaultTranslation: "fern league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 10,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 7,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 13,
    league_name: LeaderboardLeagues.FERN,
  },
  [LeaderboardLeagues.IVY]: {
    leagueTier: 4,
    leagueIcon: "/images/leaderboard_leagues/ivy_league.svg",
    translationKey: "ivy_league",
    defaultTranslation: "ivy league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 7,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 7,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 16,
    league_name: LeaderboardLeagues.IVY,
  },
  [LeaderboardLeagues.LILY_PAD]: {
    leagueTier: 5,
    leagueIcon: "/images/leaderboard_leagues/lily_pad_league.svg",
    translationKey: "lily_pad_league",
    defaultTranslation: "lily pad league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 7,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 7,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 16,
    league_name: LeaderboardLeagues.LILY_PAD,
  },
  [LeaderboardLeagues.SILVERLEAF]: {
    leagueTier: 6,
    leagueIcon: "/images/leaderboard_leagues/silverleaf_league.svg",
    translationKey: "silverleaf_league",
    defaultTranslation: "silverleaf league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 7,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 7,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 16,
    league_name: LeaderboardLeagues.SILVERLEAF,
  },
  [LeaderboardLeagues.GOLDENLEAF]: {
    leagueTier: 7,
    leagueIcon: "/images/leaderboard_leagues/goldenleaf_league.svg",
    translationKey: "goldenleaf_league",
    defaultTranslation: "goldenleaf league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 5,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 7,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 18,
    league_name: LeaderboardLeagues.GOLDENLEAF,
  },
  [LeaderboardLeagues.CELESTIAL_LEAF]: {
    leagueTier: 8,
    leagueIcon: "/images/leaderboard_leagues/celestial_leaf_league.svg",
    translationKey: "celestial_leaf_league",
    defaultTranslation: "celestial leaf league",
    numberOfTopUsersWhoArePromotedToTheNextLeague: 0,
    numberOfBottomUsersWhoAreDemotedToThePreviousLeague: 7,
    numberOfMiddleUsersWhoRemainInTheSameLeague: 23,
    league_name: LeaderboardLeagues.CELESTIAL_LEAF,
  },
};
